import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Volume2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { playNotificationSound } from '@/lib/notification-display';
import {
  loadSoundSettings,
  saveSoundSettings,
  MAX_REPEATS,
  type NotificationSoundSettings,
} from '@/lib/notification-sound-settings';

/**
 * Bell popover in the conversations header: lets each operator tune the handoff chime — on/off,
 * how many times it rings per notification, and volume — persisted per browser.
 */
export function NotificationSoundSettingsPopover() {
  const { t } = useTranslation('conversations');
  const [settings, setSettings] = useState<NotificationSoundSettings>(() => loadSoundSettings());

  const update = (patch: Partial<NotificationSoundSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveSoundSettings(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('soundSettings.aria')}
          className="text-chat-text-muted hover:bg-chat-border/30"
        >
          {settings.enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4 opacity-60" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-4">
        <p className="text-sm font-semibold">{t('soundSettings.title')}</p>

        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm">{t('soundSettings.enable')}</p>
            <p className="text-xs text-muted-foreground">{t('soundSettings.enableHint')}</p>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={(enabled) => update({ enabled })} />
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className={`text-sm ${settings.untilAttended ? 'text-muted-foreground' : ''}`}>
            {t('soundSettings.repeats')}
          </p>
          <Select
            value={String(settings.repeats)}
            onValueChange={(value) => update({ repeats: Number(value) })}
            disabled={!settings.enabled || settings.untilAttended}
          >
            <SelectTrigger className="h-8 w-24 text-sm">
              <SelectValue />
            </SelectTrigger>
            {/* The popover sits at z-[9999]; the select's default z-50 would render its list
                behind it. Bumped here rather than in the shared component so no other Select
                changes stacking. */}
            <SelectContent className="z-[10000]">
              {Array.from({ length: MAX_REPEATS }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {t('soundSettings.times', { count: n })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm">{t('soundSettings.untilAttended')}</p>
            <p className="text-xs text-muted-foreground">{t('soundSettings.untilAttendedHint')}</p>
          </div>
          <Switch
            checked={settings.untilAttended}
            onCheckedChange={(untilAttended) => update({ untilAttended })}
            disabled={!settings.enabled}
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-sm flex items-center gap-1.5">
              <Volume2 className="h-3.5 w-3.5" /> {t('soundSettings.volume')}
            </p>
            <span className="text-xs text-muted-foreground">{Math.round(settings.volume * 100)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(settings.volume * 100)}
            onChange={(e) => update({ volume: Number(e.target.value) / 100 })}
            disabled={!settings.enabled}
            className="w-full accent-chat-accent"
            aria-label={t('soundSettings.volume')}
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => playNotificationSound(true)}
        >
          <Play className="h-3.5 w-3.5 mr-1.5" /> {t('soundSettings.test')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
